<template>
    <div class="container py-5">
        <div class="mb-4 d-flex justify-content-between align-items-center">
            <h2 class="fw-bold">Activity Log Viewer</h2>
            <button type="button" class="btn btn-danger" @click="openClearLogsModal">
                Clear Logs
            </button>
        </div>
        <div class="alertContainer" id="alertContainer"></div>

        <!-- Filters -->
        <div class="card mb-4 shadow-sm">
            <div class="card-body">
                <div class="row gy-3">
                    <div class="col-md-3">
                        <label class="form-label" for="user_id">User</label>
                        <select id="user_id" v-model="filters.user_id" class="form-select">
                            <option value="">All</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label" for="loggable_type">Model Type</label>
                        <select id="loggable_type" v-model="filters.loggable_type" class="form-select">
                            <option value="">All</option>
                            <option v-for="type in loggables" :key="type" :value="type">{{ type }}</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label" for="start_date">Start Date</label>
                        <input id="start_date" v-model="filters.start_date" class="form-control" type="date">
                    </div>
                    <div class="col-md-3">
                        <label class="form-label" for="end_date">End Date</label>
                        <input id="end_date" v-model="filters.end_date" class="form-control" type="date">
                    </div>
                </div>
                <div class="d-flex justify-content-end gap-2 mt-4">
                    <button class="btn btn-outline-danger" @click="resetFilters">Reset Filters</button>
                    <button class="btn btn-primary" @click="fetchLogs">Apply Filters</button>
                </div>
            </div>
        </div>

        <!-- Logs Table -->
        <div class="card shadow-sm">
            <div class="card-body">
                <div v-if="logs.length">
                    <div class="table-responsive">
                        <table class="table table-striped align-middle">
                            <thead class="table-dark">
                            <tr class="text-nowrap">
                                <th>ID</th>
                                <th>Activity</th>
                                <th>User</th>
                                <th>Model</th>
                                <th>Model ID</th>
                                <th>Details</th>
                                <th>Date</th>
                            </tr>
                            </thead>
                            <tbody :class="{ 'overlay-spinner': loading }">
                            <tr v-for="log in logs" :key="log.id">
                                <td>{{ log.id }}</td>
                                <td>{{ log.activity_string }}</td>
                                <td>{{ log.user?.name || 'System' }}</td>
                                <td>{{ log.loggable_type }}</td>
                                <td>#{{ log.loggable_id }}</td>
                                <td>
                                    <button class="btn btn-sm btn-outline-primary" @click="openModal(log)">
                                        View Details
                                    </button>
                                </td>
                                <td>{{ new Date(log.created_at).toLocaleString() }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-else class="text-center py-5">
                    <p class="text-muted">No logs found for the selected filters.</p>
                </div>
            </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex justify-content-between align-items-center mt-4">
            <div>
                <button :disabled="!pagination.hasPrev" class="btn btn-outline-primary me-2" @click="prevPage">
                    Previous
                </button>
                <button :disabled="!pagination.hasNext" class="btn btn-outline-primary" @click="nextPage">
                    Next
                </button>
            </div>
            <div>
                <span class="text-muted">
                    Page {{ pagination.currentPage }} of {{ pagination.totalPages }}
                </span>
            </div>
        </div>

        <!-- Clear Logs Modal -->
        <div class="modal fade" id="clearLogsModal" tabindex="-1" aria-labelledby="clearLogsModalLabel" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="clearLogsModalLabel">Clear Logs</h5>
                        <button type="button" class="btn-close" @click="closeClearLogsModal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-3">
                            <label for="clearLogsPeriod" class="form-label">Select Time Period</label>
                            <select v-model="clearLogsPeriod" id="clearLogsPeriod" class="form-select">
                                <option v-for="option in clearLogsPeriodOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                        <div v-if="clearLogsPeriod" class="alert alert-warning" role="alert">
                            Are you sure you want to clear logs older than {{ clearLogsPeriod }}?
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-primary" @click="closeClearLogsModal">Cancel</button>
                        <button type="button" class="btn btn-danger" @click="clearLogs" :disabled="!clearLogsPeriod">
                            Clear Logs
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Log Details Modal -->
        <div id="logDetailsModal" aria-hidden="true" aria-labelledby="logDetailsModalLabel" class="modal fade"
             tabindex="-1">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content shadow-sm rounded-4 overflow-hidden">
                    <div class="modal-header bg-light border-bottom">
                        <h5 class="modal-title fw-bold">Log Details</h5>
                        <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" type="button"></button>
                    </div>

                    <div class="modal-body p-4" style="max-height: 80vh; overflow-y: auto;">
                        <div class="row gy-4">
                            <div class="col-md-4">
                                <h6 class="fw-semibold">Original Values</h6>
                                <div class="log-box">
                                    <pre>{{ JSON.stringify(selectedLog.original_values, null, 2) }}</pre>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <h6 class="fw-semibold">New Values</h6>
                                <div class="log-box">
                                    <pre>{{ JSON.stringify(selectedLog.new_values, null, 2) }}</pre>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <h6 class="fw-semibold">Changes</h6>
                                <div class="log-box">
                                    <pre>{{ JSON.stringify(selectedLog.changes, null, 2) }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer bg-light border-top">
                        <button class="btn btn-danger" data-bs-dismiss="modal" type="button">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            logs: [],
            users: [],
            loading: true,
            loggables: [],
            filters: {
                user_id: '',
                loggable_type: '',
                start_date: '',
                end_date: ''
            },
            selectedLog: {},
            pagination: {
                currentPage: 1,
                perPage: 20,
                totalElements: 0,
                totalPages: 1,
                hasNext: false,
                hasPrev: false
            },
            clearLogsPeriod: '',
            clearLogsPeriodOptions: [
                { value: '3m', label: '3 Months' },
                { value: '6m', label: '6 Months' },
                { value: '1y', label: '1 Year' }
            ]
        };
    },
    computed: {
        filterParams() {
            return {...this.filters};
        }
    },
    created() {
        this.fetchLogs();
    },
    methods: {
        async fetchLogs() {
            try {
                this.loading = true;
                const params = new URLSearchParams({
                    ...this.filters,
                    page: this.pagination.currentPage,
                    perPage: this.pagination.perPage
                }).toString();
                const response = await fetch(`/get-activity-logs?${params}`);
                const result = await response.json();
                this.logs = result.data;
                this.users = result.users;
                this.loggables = result.loggable;
                this.pagination = result.pagination;
                this.loading = false;
            } catch (error) {
                console.error('Error fetching logs:', error);
            }
        },
        resetFilters() {
            this.filters = {
                user_id: '',
                loggable_type: '',
                start_date: '',
                end_date: ''
            };
            this.pagination.currentPage = 1;
            this.fetchLogs();
        },
        openModal(log) {
            this.selectedLog = log;
            new bootstrap.Modal(document.getElementById('logDetailsModal')).show();
        },
        nextPage() {
            if (this.pagination.hasNext) {
                this.pagination.currentPage++;
                this.fetchLogs();
            }
        },
        prevPage() {
            if (this.pagination.hasPrev) {
                this.pagination.currentPage--;
                this.fetchLogs();
            }
        },
        openClearLogsModal() {
            const clearLogsModal = document.getElementById('clearLogsModal');
            clearLogsModal.classList.add('show');
            clearLogsModal.setAttribute('aria-hidden', 'false');
            clearLogsModal.style.display = 'block';
        },
        closeClearLogsModal() {
            const clearLogsModal = document.getElementById('clearLogsModal');
            clearLogsModal.classList.remove('show');
            clearLogsModal.setAttribute('aria-hidden', 'true');
            clearLogsModal.style.display = 'none';
        },
        async clearLogs() {
            try {
                const response = await fetch(`/clear-activity-logs`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    },
                    body: JSON.stringify({ period: this.clearLogsPeriod })
                });
                const result = await response.json();
                if (result.success) {
                    this.showSuccessMessage(result.message);
                    this.closeClearLogsModal();
                    await this.fetchLogs();
                } else {
                    this.showFailureMessage('Failed to clear logs.');
                }
            } catch (error) {
                console.error('Error clearing logs:', error);
                this.showFailureMessage('An error occurred while clearing logs.');
            }
        },
        showSuccessMessage(message) {
            this.showMessage('alert-success', message);
        },
        showFailureMessage(message) {
            this.showMessage('alert-danger', message);
        },
        showMessage(type, message) {
            const alertContainer = document.getElementById('alertContainer') || document.createElement('div');
            if (!document.getElementById('alertContainer')) {
                alertContainer.id = 'alertContainer';
                document.body.appendChild(alertContainer);
            }
            const alert = document.createElement('div');
            alert.className = `alert ${type} alert-dismissible fade show`;
            alert.role = 'alert';
            alert.innerHTML = `${message} <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>`;
            alertContainer.appendChild(alert);

            setTimeout(() => {
                alert.remove();
            }, 5000);
        }
    }
};
</script>

<style scoped>
.table td,
.table th {
    vertical-align: middle;
}

.modal-body pre {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 10px;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-word;
}

.table tbody {
    font-size: 13px;
}

.log-box {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    padding: 10px;
    font-size: 13px;
    max-height: 300px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
}

.modal-content {
    border-radius: 1rem;
}
</style>
